import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import { registerUserCafe, geocodeAddress } from '../../services/cafeService';
import DataLoader from '../../components/DataLoader';
import LabeledTextInput from '../../components/PostThreads/LabeledTextInput';
import LabeledTextarea from '../../components/PostThreads/LabeledTextarea';
import InputAdornment from '@mui/material/InputAdornment';

const CafeRegistrationModal = ({ open, onClose, currentUser, isMobile }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    cafeIntroduction: '',
    cafeUrl: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleAddressGeocoding = async () => {
    if (!formData.address) {
      setErrors((prev) => ({ ...prev, address: '주소를 입력해주세요' }));
      return;
    }

    setIsGeocoding(true);
    try {
      const coords = await geocodeAddress(formData.address);
      if (coords) {
        setCoordinates(coords);
        setErrors((prev) => ({ ...prev, address: '' }));
      } else {
        setErrors((prev) => ({ ...prev, address: '주소를 좌표로 변환할 수 없습니다' }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, address: '주소 변환 중 오류가 발생했습니다' }));
    } finally {
      setIsGeocoding(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '카페 이름은 필수입니다';
    }

    if (!formData.address.trim()) {
      newErrors.address = '주소는 필수입니다';
    }

    if (!formData.cafeIntroduction.trim()) {
      newErrors.cafeIntroduction = '카페 소개글은 필수입니다';
    } else {
      // 글자수 제한 (최대 50자)
      const actualLength = [...formData.cafeIntroduction].length;
      if (actualLength > 50) {
        newErrors.cafeIntroduction = '카페 소개글은 50자를 초과할 수 없습니다';
      }
    }

    // URL 검증 제거 - 사용자가 입력한 것 그대로 허용

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await registerUserCafe(formData, currentUser);
      alert('카페가 성공적으로 등록되었습니다.');
      onClose();
      // Reset form
      setFormData({
        name: '',
        address: '',
        description: '',
        cafeIntroduction: '',
        cafeUrl: '',
      });
      setCoordinates(null);
    } catch (error) {
      console.error('Cafe registration error:', error);
      setSubmitError(error.message || '카페 등록 중 오류가 발생했습니다');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 메시지 결정
  const getLoadingMessage = () => {
    if (isGeocoding) return "주소를 좌표로 변환하는 중...";
    if (isSubmitting) return "카페 등록 및 Threads 포스팅 중...";
    return "";
  };

  const getLoadingSubMessage = () => {
    if (isGeocoding) return "정확한 위치 정보를 생성하고 있습니다";
    if (isSubmitting) return "주소 변환 → Threads 포스팅 → 카페 등록 순으로 진행됩니다";
    return "";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
          }
        }}
      >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        카페 등록하기
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}

          <LabeledTextInput
            label="카페 이름"
            placeholder="예: 스레드 카페 신촌점"
            value={formData.name}
            onChange={(e) => handleChange({ target: { name: 'name', value: e.target.value } })}
            required={true}
            error={errors.name}
          />

          <Box>
            <LabeledTextInput
              label="카페 주소"
              placeholder="도로명 주소"
              value={formData.address}
              onChange={(e) => handleChange({ target: { name: 'address', value: e.target.value } })}
              required={true}
              error={errors.address || null}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleAddressGeocoding}
                    disabled={isGeocoding || !formData.address}
                    edge="end"
                  >
                    {isGeocoding ? <CircularProgress size={20} /> : <MapIcon />}
                  </IconButton>
                </InputAdornment>
              }
            />
            {/* 좌표가 생성된 경우에만 보조 텍스트로 표시 */}
            {coordinates ? (
              <Box sx={{ mt: 0.5, color: 'text.secondary', fontSize: 12 }}>
                좌표: {coordinates.lat.toFixed(4)}, {coordinates.lng.toFixed(4)}
              </Box>
            ) : null}
          </Box>

          <LabeledTextInput
            label="카페 설명"
            placeholder="1줄 소개를 적어주세요."
            value={formData.description}
            onChange={(e) => handleChange({ target: { name: 'description', value: e.target.value } })}
          />

          <LabeledTextarea
            label="카페 소개글 (Threads에 포스팅됩니다)"
            placeholder="예: 조용한 독서 공간이 있는 연남동 북카페입니다"
            value={formData.cafeIntroduction}
            onChange={(e) => handleChange({ target: { name: 'cafeIntroduction', value: e.target.value } })}
            maxLength={50}
            minRows={3}
            maxRows={5}
            error={errors.cafeIntroduction}
          />

          <LabeledTextInput
            label="링크 (카페 인스타 혹은 네이버 맵)"
            placeholder="예: https://instagram.com/cafe_example"
            value={formData.cafeUrl}
            onChange={(e) => handleChange({ target: { name: 'cafeUrl', value: e.target.value } })}
            error={errors.cafeUrl}
          />

          <Alert severity="info">
            ✓ 카페 소개글이 Threads에 자동으로 포스팅됩니다<br/>
            ✓ 등록하신 카페는 지도에 즉시 표시됩니다<br/>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          취소
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !formData.name || !formData.address || !formData.cafeIntroduction}
        >
          {isSubmitting ? <CircularProgress size={20} /> : '등록하기'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* 로딩 상태 표시 */}
    <DataLoader
      msg={getLoadingMessage()}
      subMessage={getLoadingSubMessage()}
      isLoad={isGeocoding || isSubmitting}
      showElapsedTime={false}
      isLogin={true}
      isXL={false}
      isPublicRoute={false}
    />
  </>
  );
};

export default CafeRegistrationModal;